import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { api } from './api';
import { Empty, money, Tag, type Props } from './ui';

/** Typed numbers, separated by spaces, commas or slashes; names the first one that is not a mobile. */
export function splitPhones(text: string) {
  const phones = text.split(/[\s,，、/;；]+/).filter(Boolean);
  const bad = phones.find((v) => !/^1\d{10}$/.test(v));
  if (bad) throw new Error(`“${bad}”不是 11 位手机号`);
  if (phones.length > 3) throw new Error('对接手机号最多 3 个');
  return phones;
}

const FIELDS = [
  { name: 'name', label: '名称', wide: true },
  { name: 'contact', label: '联系人' },
  {
    name: 'phones',
    label: '对接手机号',
    helper: '签收送货单、确认对账单时核验，最多 3 个，空格分隔',
  },
  { name: 'note', label: '备注', wide: true },
];

/** Adds a counterparty (no argument) or edits one; the list reloads through `p.version`. */
export const editCounterparty = (p: Props, record?: any) =>
  p.open({
    title: record ? `编辑往来单位 · ${record.name}` : '新建往来单位',
    description: record
      ? '改名后，这个单位的历史单据会一起显示新名称。'
      : '未入驻平台的客户或供应商。新建单据时直接输入新名称，也会自动建档。',
    fields: FIELDS,
    form: record
      ? {
          name: record.name,
          contact: record.contact,
          phones: record.receiver_phones.join(' '),
          note: record.note,
        }
      : { name: '', contact: '', phones: '', note: '' },
    submit: async ({ phones, ...f }: any) =>
      api(record ? `/counterparties/${record.id}` : '/counterparties', {
        ...f,
        receiverPhones: splitPhones(phones),
      }),
  });

/**
 * Customers and suppliers not on the platform: one record each, so self-recorded orders and
 * what is owed add up per counterparty. Duplicates from typing are merged here. Adding one is
 * the page heading's action.
 */
export default function CounterpartyPanel(p: Props) {
  const [records, setRecords] = useState<any[] | null>(null);
  useEffect(() => {
    api<any[]>('/counterparties')
      .then(setRecords)
      .catch((e) => p.setError(e.message));
  }, [p.version, p.workspace]);

  const edit = (record?: any) => editCounterparty(p, record);
  const merge = (record: any) =>
    p.open({
      title: `合并往来单位 · ${record.name}`,
      description: `把“${record.name}”的 ${record.orders} 张单据并入另一个单位，并删除这条档案。适用于输入时产生的重名、错字。`,
      fields: [
        {
          name: 'intoId',
          label: '并入',
          wide: true,
          options: (records ?? [])
            .filter((r) => r.id !== record.id)
            .map((r) => ({ value: r.id, label: r.name })),
        },
      ],
      form: { intoId: (records ?? []).find((r) => r.id !== record.id)?.id ?? '' },
      submit: (f: any) => api(`/counterparties/${record.id}/merge`, f),
    });

  const amount = (value: string, tone?: 'warning') =>
    Number(value) > 0 ? (
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 650,
          whiteSpace: 'nowrap',
          color: tone ? 'warning.dark' : undefined,
        }}
      >
        {money(value)}
      </Typography>
    ) : (
      <Typography color="text.disabled" sx={{ fontSize: 13 }}>
        —
      </Typography>
    );

  return (
    <Paper>
      {records?.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            sx={{ '& th, & td': { px: 1.5 }, '& th:first-of-type, & td:first-of-type': { pl: 3 } }}
          >
            <TableHead>
              <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                <TableCell>单位</TableCell>
                <TableCell align="right">单据</TableCell>
                <TableCell align="right">待对账</TableCell>
                <TableCell align="right">应收</TableCell>
                <TableCell align="right">应付</TableCell>
                <TableCell align="right" sx={{ pr: 3 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ width: '36%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{r.name}</Typography>
                      {r.partner_name && <Tag tone="success">已开通联仓</Tag>}
                    </Box>
                    <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }}>
                      {[
                        r.contact,
                        r.receiver_phones.join('、'),
                        r.partner_name && `联仓企业：${r.partner_name}`,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '未填写联系人'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                    {[r.sales && `销 ${r.sales}`, r.purchases && `采 ${r.purchases}`]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {amount(r.unbilled)}
                    {r.unbilled_orders > 0 && (
                      <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
                        {r.unbilled_orders} 单
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{amount(r.receivable, 'warning')}</TableCell>
                  <TableCell align="right">{amount(r.payable, 'warning')}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 3 }}>
                    <Button size="small" onClick={() => edit(r)}>
                      编辑
                    </Button>
                    {records.length > 1 && (
                      <Button size="small" color="inherit" onClick={() => merge(r)}>
                        合并
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Empty>
          {records ? '还没有未入驻单位。新建采购或销售时直接输入名称即可建档。' : '正在加载…'}
        </Empty>
      )}
    </Paper>
  );
}
